import React, { useState, useRef, useCallback } from 'react';
import {
  Box, Button, Typography, TextField, Paper, IconButton,
  ToggleButton, ToggleButtonGroup, LinearProgress, Chip,
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import WorkIcon from '@mui/icons-material/Work';
import ArticleIcon from '@mui/icons-material/Article';
import BarChartIcon from '@mui/icons-material/BarChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import JSZip from 'jszip';
import { AnalysisMode, CVFile } from '../types';

interface Props {
  onAnalyze: (mode: AnalysisMode, jd: string, title: string, files: CVFile[]) => void;
  extractTextFromPDF: (file: File) => Promise<string>;
  error: string;
}

interface FileEntry {
  file: File;
  status: 'pending' | 'reading' | 'done' | 'error';
  text: string;
}

const SetupPanel: React.FC<Props> = ({ onAnalyze, extractTextFromPDF, error }) => {
  const muiTheme = useMuiTheme();
  const isDark = muiTheme.palette.mode === 'dark';

  const [mode, setMode] = useState<AnalysisMode>('jd');
  const [jd, setJd] = useState('');
  const [title, setTitle] = useState('');
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const cvInputRef = useRef<HTMLInputElement>(null);
  const jdInputRef = useRef<HTMLInputElement>(null);

  const processFiles = useCallback(async (files: File[]) => {
    // Expand any ZIPs first
    const expanded: File[] = [];
    for (const f of files) {
      if (f.name.endsWith('.zip') || f.type === 'application/zip' || f.type === 'application/x-zip-compressed') {
        try {
          const zip = await JSZip.loadAsync(f);
          const promises: Promise<void>[] = [];
          zip.forEach((relativePath, zipEntry) => {
            if (!zipEntry.dir && (relativePath.endsWith('.pdf') || relativePath.endsWith('.txt'))) {
              const name = relativePath.split('/').pop() || relativePath;
              promises.push(
                zipEntry.async('blob').then(blob => {
                  expanded.push(new File([blob], name, { type: relativePath.endsWith('.pdf') ? 'application/pdf' : 'text/plain' }));
                })
              );
            }
          });
          await Promise.all(promises);
        } catch { /* skip corrupt ZIPs */ }
      } else if (f.type === 'application/pdf' || f.name.endsWith('.pdf') || f.name.endsWith('.txt')) {
        expanded.push(f);
      }
    }

    if (!expanded.length) return;

    const newEntries: FileEntry[] = expanded.map(f => ({ file: f, status: 'reading', text: '' }));
    setEntries(prev => {
      const existing = new Set(prev.map(e => e.file.name));
      return [...prev, ...newEntries.filter(e => !existing.has(e.file.name))];
    });

    for (const entry of newEntries) {
      try {
        const text = entry.file.type === 'application/pdf' || entry.file.name.endsWith('.pdf')
          ? await extractTextFromPDF(entry.file)
          : await entry.file.text();
        setEntries(prev => prev.map(e =>
          e.file.name === entry.file.name ? { ...e, status: 'done', text } : e
        ));
      } catch {
        setEntries(prev => prev.map(e =>
          e.file.name === entry.file.name ? { ...e, status: 'error' } : e
        ));
      }
    }
  }, [extractTextFromPDF]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    processFiles(Array.from(e.dataTransfer.files)); // ZIPs handled inside processFiles
  }, [processFiles]);

  const handleJDFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = file.type === 'application/pdf' || file.name.endsWith('.pdf')
        ? await extractTextFromPDF(file)
        : await file.text();
      setJd(text);
    } catch { /* ignore */ }
    if (jdInputRef.current) jdInputRef.current.value = '';
  }, [extractTextFromPDF]);

  const remove = (name: string) => setEntries(prev => prev.filter(e => e.file.name !== name));

  const readyFiles = entries.filter(e => e.status === 'done');
  const readingCount = entries.filter(e => e.status === 'reading').length;

  const canAnalyze =
    readyFiles.length > 0 &&
    (mode === 'jd' ? jd.trim().length > 20 : mode === 'title' ? title.trim().length > 0 : true);

  const handleAnalyze = () => {
    onAnalyze(mode, jd, title, readyFiles.map(e => ({ filename: e.file.name, text: e.text })));
  };

  const glassStyle = {
    p: 3,
    borderRadius: 3,
    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.72)',
    border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(255,255,255,0.9)',
    backdropFilter: 'blur(20px)',
  };

  return (
    <Box className="fade-up" sx={{ maxWidth: 760, mx: 'auto' }}>
      {/* Hero */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{
          fontWeight: 850, letterSpacing: '-0.04em',
          color: isDark ? '#FFF8EF' : '#171615',
          fontSize: { xs: '2rem', sm: '2.6rem' },
        }}>
          Screen smarter.{' '}
          <Box component="span" sx={{ color: 'primary.main' }}>Shortlist faster.</Box>
        </Typography>
        <Typography variant="body1" sx={{ mt: 1.5, color: 'text.secondary', maxWidth: 520, mx: 'auto' }}>
          Upload your CVs, set a role or job description, and get instant AI-ranked results —
          match scores, gaps, nationalities, and a full breakdown.
        </Typography>
      </Box>

      {/* Mode selector */}
      <Paper elevation={0} sx={{ ...glassStyle, mb: 2.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', mb: 1.5, display: 'block' }}>
          Analysis mode
        </Typography>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, v) => v && setMode(v)}
          fullWidth
          size="small"
          sx={{ gap: 1, '& .MuiToggleButtonGroup-grouped': { border: '1px solid !important', borderRadius: '10px !important', mx: 0 } }}
        >
          {[
            { value: 'jd',      label: 'Job Description', icon: <ArticleIcon fontSize="small" /> },
            { value: 'title',   label: 'Job Title',       icon: <WorkIcon fontSize="small" /> },
            { value: 'general', label: 'General Analysis',icon: <BarChartIcon fontSize="small" /> },
          ].map(opt => (
            <ToggleButton
              key={opt.value} value={opt.value}
              sx={{
                gap: 0.75, fontWeight: 600, fontSize: '0.82rem',
                borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, #FF8428, #FFB06A) !important',
                  color: 'white !important',
                  borderColor: 'transparent !important',
                },
              }}
            >
              {opt.icon}{opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box sx={{ mt: 2.5 }}>
          {mode === 'jd' && (
            <>
              <TextField
                multiline rows={5} fullWidth
                placeholder="Paste the full job description here…"
                value={jd} onChange={e => setJd(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <Button
                component="label" size="small" variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={{ fontSize: '0.78rem' }}
              >
                Or upload JD as PDF / TXT
                <input ref={jdInputRef} type="file" hidden accept=".pdf,.txt" onChange={handleJDFile} />
              </Button>
            </>
          )}
          {mode === 'title' && (
            <TextField
              fullWidth
              placeholder="e.g. Senior Project Manager, HR Coordinator, Sales Executive…"
              label="Job Title"
              value={title} onChange={e => setTitle(e.target.value)}
            />
          )}
          {mode === 'general' && (
            <Box sx={{ py: 1.5, px: 2, borderRadius: 2, background: isDark ? 'rgba(255,132,40,0.08)' : 'rgba(255,132,40,0.06)', border: '1px solid rgba(255,132,40,0.2)' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No role required — get a general breakdown of each candidate's experience, skills, nationality, and overall profile.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* CV Upload */}
      <Paper elevation={0} sx={{ ...glassStyle, mb: 2.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', mb: 1.5, display: 'block' }}>
          Candidate CVs
        </Typography>

        <Box
          className={`drop-zone${dragOver ? ' drag-over' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => cvInputRef.current?.click()}
          sx={{
            p: 4, textAlign: 'center', cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': { borderColor: 'primary.main', background: 'rgba(255,132,40,0.04)' },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1 }}>
            <UploadFileIcon sx={{ fontSize: 36, color: 'primary.main', opacity: 0.8 }} />
            <FolderZipIcon sx={{ fontSize: 36, color: 'primary.main', opacity: 0.5 }} />
          </Box>
          <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Drop CV files here or <Box component="span" sx={{ color: 'primary.main' }}>browse</Box>
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
            PDF · TXT · ZIP — Multiple files, no limit
          </Typography>
          <input
            ref={cvInputRef} type="file" hidden multiple accept=".pdf,.txt,.zip"
            onChange={e => { processFiles(Array.from(e.target.files || [])); if (cvInputRef.current) cvInputRef.current.value = ''; }}
          />
        </Box>

        {readingCount > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Reading {readingCount} file{readingCount > 1 ? 's' : ''}…</Typography>
            <LinearProgress sx={{ mt: 0.5, borderRadius: 4 }} />
          </Box>
        )}

        {entries.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {entries.map(entry => (
              <Box key={entry.file.name} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2, py: 1, borderRadius: 2,
                background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
              }}>
                <ArticleIcon fontSize="small" sx={{ color: 'primary.main', flexShrink: 0 }} />
                <Typography variant="body2" sx={{ flex: 1, fontWeight: 500, fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {entry.file.name}
                </Typography>
                <Chip
                  size="small"
                  label={entry.status === 'reading' ? 'Reading…' : entry.status === 'done' ? 'Ready' : 'Error'}
                  color={entry.status === 'done' ? 'success' : entry.status === 'error' ? 'error' : 'default'}
                  sx={{ fontSize: '0.72rem', height: 22, fontWeight: 700 }}
                />
                <IconButton size="small" onClick={() => remove(entry.file.name)} sx={{ color: 'text.secondary', p: 0.5 }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {error && (
        <Box sx={{ mb: 2, p: 2, borderRadius: 2, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
          <Typography variant="body2" color="error">{error}</Typography>
        </Box>
      )}

      <Button
        fullWidth variant="contained" size="large"
        disabled={!canAnalyze}
        onClick={handleAnalyze}
        startIcon={<AutoAwesomeIcon />}
        sx={{
          py: 1.8, borderRadius: 3, fontWeight: 800, fontSize: '1rem',
          background: canAnalyze ? 'linear-gradient(135deg, #FF8428, #FFB06A)' : undefined,
          boxShadow: canAnalyze ? '0 8px 32px rgba(255,132,40,0.35)' : 'none',
          '&:hover': { background: 'linear-gradient(135deg, #e0721e, #FF8428)', boxShadow: '0 12px 40px rgba(255,132,40,0.45)' },
        }}
      >
        {readyFiles.length > 0
          ? `Analyze ${readyFiles.length} CV${readyFiles.length > 1 ? 's' : ''}`
          : 'Upload CVs to begin'}
      </Button>
    </Box>
  );
};

export default SetupPanel;
